import { Meal, Symptom, Exercise } from './types';
import { localStore } from './storage';
import { bulkStore } from './bulkStore';

export interface SyncQueueItem {
  id: string;
  entityId: string;
  type: 'meal' | 'symptom' | 'exercise';
  action: 'add' | 'update' | 'delete';
  data?: any;
  userId: string;
  createdAt: number;
  retryCount: number;
}

const QUEUE_KEY_PREFIX = 'digestlog_pending_sync_v2';
const VAULT_KEY_PREFIX = 'digestlog_safety_vault_v2';
const TOMBSTONES_KEY_PREFIX = 'digestlog_tombstones_v2';

function getQueueKey(userId: string): string {
  const uid = userId || 'usr_harry';
  return `${QUEUE_KEY_PREFIX}_${uid}`;
}

function getVaultKey(type: string, userId: string): string {
  const uid = userId || 'usr_harry';
  return `${VAULT_KEY_PREFIX}_${type}_${uid}`;
}

function getTombstonesKey(userId: string): string {
  const uid = userId || 'usr_harry';
  return `${TOMBSTONES_KEY_PREFIX}_${uid}`;
}

type VaultType = 'meals' | 'symptoms' | 'exercises';
type Vault = Record<string, any>;
const VAULT_TYPES: VaultType[] = ['meals', 'symptoms', 'exercises'];

/*
 * The vault is a full second copy of every meal, so on the phone it lives in native app storage
 * rather than WebKit's ~5 MB localStorage. Native storage is async, so the vault is held in memory
 * once loaded; writes made before it has loaded wait for it, so a partial vault never overwrites
 * the full one. Older builds kept it in localStorage under the same key: that copy is read as a
 * fallback and only removed once the native copy has been written and read back.
 */
const vaultCache = new Map<string, Vault>();
const vaultLoading = new Map<string, Promise<Vault>>();
const vaultWrites = new Map<string, { running: boolean; dirty: boolean }>();

/** Parses a stored vault; null means the value exists but isn't a readable vault. */
function parseVault(raw: string | null): Vault | null {
  if (raw === null) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Union of two vaults; where both hold an id, the more recently saved copy wins. */
function mergeVaults(a: Vault, b: Vault): Vault {
  const merged: Vault = { ...a };
  for (const [id, item] of Object.entries(b)) {
    const existing = merged[id];
    if (!existing || (item?._vault_saved_at || 0) > (existing?._vault_saved_at || 0)) {
      merged[id] = item;
    }
  }
  return merged;
}

async function loadVault(key: string): Promise<Vault> {
  const legacyRaw = localStorage.getItem(key);
  const legacy = parseVault(legacyRaw);
  if (!bulkStore.isNative()) return legacy || {};

  let native: Vault = {};
  try {
    native = parseVault(await bulkStore.get(key)) || {};
  } catch (e) {
    console.warn('Failed to read vault from native storage:', e);
  }
  // Nothing to move, or an unreadable old copy that is left untouched.
  if (legacyRaw === null || legacy === null) return native;

  // Move the localStorage copy across: write, read back, and only then free the old key.
  const merged = mergeVaults(native, legacy);
  const serialized = JSON.stringify(merged);
  if (await bulkStore.setVerified(key, serialized)) {
    localStorage.removeItem(key);
    console.info(`Moved ${key} to native storage (${Object.keys(merged).length} items)`);
  } else {
    console.warn(`Could not move ${key} to native storage; keeping the localStorage copy`);
  }
  return merged;
}

function ensureVault(key: string): Promise<Vault> {
  const cached = vaultCache.get(key);
  if (cached) return Promise.resolve(cached);
  if (!bulkStore.isNative()) {
    // localStorage is synchronous, so the web build loads the vault straight away.
    const vault = parseVault(localStorage.getItem(key)) || {};
    vaultCache.set(key, vault);
    return Promise.resolve(vault);
  }
  let loading = vaultLoading.get(key);
  if (!loading) {
    loading = loadVault(key).then((vault) => {
      vaultCache.set(key, vault);
      vaultLoading.delete(key);
      return vault;
    });
    vaultLoading.set(key, loading);
  }
  return loading;
}

/** Synchronous view of the vault: the loaded copy, or the legacy localStorage copy until it has loaded. */
function readVaultNow(key: string): Vault {
  const cached = vaultCache.get(key);
  if (cached) return cached;
  if (!bulkStore.isNative()) {
    ensureVault(key);
    return vaultCache.get(key) || {};
  }
  return parseVault(localStorage.getItem(key)) || {};
}

function writeVault(key: string): void {
  const vault = vaultCache.get(key);
  if (!vault) return;
  if (!bulkStore.isNative()) {
    try {
      localStorage.setItem(key, JSON.stringify(vault));
    } catch (e) {
      console.warn('Failed to backup to vault:', e);
    }
    return;
  }
  // One native write at a time; changes made meanwhile are folded into the next write.
  const state = vaultWrites.get(key) || { running: false, dirty: false };
  vaultWrites.set(key, state);
  state.dirty = true;
  if (state.running) return;
  state.running = true;
  (async () => {
    while (state.dirty) {
      state.dirty = false;
      const ok = await bulkStore.setVerified(key, JSON.stringify(vaultCache.get(key) || {}));
      if (!ok) console.warn(`Failed to save ${key} to native storage`);
    }
    state.running = false;
  })();
}

/** Applies a change to the loaded vault and saves it; waits for the vault to load first if needed. */
function updateVault(key: string, change: (vault: Vault) => boolean): void {
  const apply = (vault: Vault) => {
    try {
      if (change(vault)) writeVault(key);
    } catch (e) {
      console.warn('Failed to update vault:', e);
    }
  };
  const cached = vaultCache.get(key);
  if (cached) apply(cached);
  else ensureVault(key).then(apply, (e) => console.warn('Failed to load vault:', e));
}

export const syncEngine = {
  /** Loads (and on the phone, migrates) the safety vaults. Safe to call repeatedly. */
  async whenVaultsReady(userId: string): Promise<void> {
    await Promise.all(VAULT_TYPES.map((type) => ensureVault(getVaultKey(type, userId)).catch(() => ({}))));
  },

  getTombstones(userId: string): Set<string> {
    try {
      const raw = localStorage.getItem(getTombstonesKey(userId));
      if (!raw) return new Set<string>();
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? new Set<string>(parsed) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  },

  addTombstone(id: string, userId: string): void {
    if (!id || typeof id !== 'string') return;
    const cleanId = id.trim();
    if (!cleanId) return;
    try {
      const tombstones = this.getTombstones(userId);
      tombstones.add(cleanId);
      localStorage.setItem(getTombstonesKey(userId), JSON.stringify(Array.from(tombstones)));
    } catch (e) {
      console.warn('Failed to add tombstone:', e);
    }
  },

  isTombstoned(id: string, userId: string): boolean {
    if (!id || typeof id !== 'string') return false;
    return this.getTombstones(userId).has(id.trim());
  },

  deleteFromVault(type: 'meals' | 'symptoms' | 'exercises', id: string, userId: string): void {
    if (!id || typeof id !== 'string') return;
    const cleanId = id.trim();
    updateVault(getVaultKey(type, userId), (vault) => {
      if (!(cleanId in vault)) return false;
      delete vault[cleanId];
      return true;
    });
  },

  getQueue(userId: string): SyncQueueItem[] {
    try {
      const raw = localStorage.getItem(getQueueKey(userId));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('Failed to read sync queue:', e);
      return [];
    }
  },

  saveQueue(queue: SyncQueueItem[], userId: string): void {
    try {
      localStorage.setItem(getQueueKey(userId), JSON.stringify(queue));
    } catch (e) {
      console.warn('Failed to save sync queue:', e);
    }
  },

  enqueue(item: Omit<SyncQueueItem, 'id' | 'createdAt' | 'retryCount'>): void {
    const queue = this.getQueue(item.userId);
    // If there's an existing mutation for the same entityId, coalesce or replace
    const filtered = queue.filter((q) => !(q.entityId === item.entityId && q.type === item.type));
    const newItem: SyncQueueItem = {
      ...item,
      id: `sync_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      createdAt: Date.now(),
      retryCount: 0,
    };
    filtered.push(newItem);
    this.saveQueue(filtered, item.userId);
  },

  dequeue(id: string, userId: string): void {
    const queue = this.getQueue(userId);
    const updated = queue.filter((q) => q.id !== id && q.entityId !== id);
    this.saveQueue(updated, userId);
  },

  /**
   * Archives snapshots in an immutable safety vault so local data can never be lost
   */
  backupToVault(type: 'meals' | 'symptoms' | 'exercises', items: any[], userId: string): void {
    if (!Array.isArray(items) || items.length === 0) return;
    const tombstones = this.getTombstones(userId);
    const savedAt = Date.now();
    updateVault(getVaultKey(type, userId), (vault) => {
      // Add/update each non-tombstoned item in vault keyed by id
      for (const item of items) {
        if (item && item.id && !tombstones.has(item.id)) {
          vault[item.id] = {
            ...item,
            _vault_saved_at: savedAt,
          };
        }
      }
      return true;
    });
  },

  /**
   * Cleans any residual mock data from the sync queue and safety vault
   */
  purgeMockData(userId: string): void {
    try {
      const isMockMeal = (m: any) =>
        ['m-1', 'm-2', 'm-3', 'm-4'].includes(m?.id) ||
        /oatmeal with blueberries|garlic parmesan fettuccine|grilled chicken salad with quinoa|green tea and sourdough/i.test(m?.description || '');
      const isMockSymptom = (s: any) => ['s-1', 's-2', 's-3'].includes(s?.id);

      // Clean Queue
      const queue = this.getQueue(userId);
      const cleanQueue = queue.filter((q) => {
        if (q.type === 'meal' && (['m-1', 'm-2', 'm-3', 'm-4'].includes(q.entityId) || isMockMeal(q.data))) return false;
        if (q.type === 'symptom' && (['s-1', 's-2', 's-3'].includes(q.entityId) || isMockSymptom(q.data))) return false;
        return true;
      });
      if (cleanQueue.length !== queue.length) {
        this.saveQueue(cleanQueue, userId);
      }

      const removeMatching = (vault: Vault, isMock: (item: any) => boolean) => {
        let changed = false;
        for (const id of Object.keys(vault)) {
          if (isMock(vault[id])) {
            delete vault[id];
            changed = true;
          }
        }
        return changed;
      };

      // Clean Meals Vault
      updateVault(getVaultKey('meals', userId), (vault) => removeMatching(vault, isMockMeal));

      // Clean Symptoms Vault
      updateVault(getVaultKey('symptoms', userId), (vault) => removeMatching(vault, isMockSymptom));
    } catch (e) {
      console.warn('Failed to purge mock data:', e);
    }
  },

  /**
   * Retrieves all historical records stored in the safety vault for recovery
   */
  getAllVaultItems<T extends { id: string }>(type: 'meals' | 'symptoms' | 'exercises', userId: string): T[] {
    try {
      const isMockMeal = (m: any) =>
        ['m-1', 'm-2', 'm-3', 'm-4'].includes(m?.id) ||
        /oatmeal with blueberries|garlic parmesan fettuccine|grilled chicken salad with quinoa|green tea and sourdough/i.test(m?.description || '');
      const isMockSymptom = (s: any) => ['s-1', 's-2', 's-3'].includes(s?.id);
      const tombstones = this.getTombstones(userId);

      const vault = readVaultNow(getVaultKey(type, userId));
      const items = (Object.values(vault) as T[]).filter((it) => it && it.id && !tombstones.has(it.id));
      if (type === 'meals') {
        return items.filter((m) => !isMockMeal(m));
      }
      if (type === 'symptoms') {
        return items.filter((s) => !isMockSymptom(s));
      }
      return items;
    } catch {
      return [];
    }
  },

  /**
   * Performs a 2-way non-destructive merge.
   * If an item exists locally but is missing in the cloud, it preserves the item
   * and queues it to be uploaded to Cloudflare KV.
   */
  mergeMeals(localMeals: Meal[], cloudMeals: Meal[], userId: string): { merged: Meal[]; newlyDiscoveredCount: number } {
    const queue = this.getQueue(userId);
    const tombstones = this.getTombstones(userId);
    const deletedMealIds = new Set(
      queue.filter((q) => q.type === 'meal' && q.action === 'delete').map((q) => q.entityId)
    );

    const isExcluded = (id: string) => deletedMealIds.has(id) || tombstones.has(id);

    const cloudMap = new Map<string, Meal>();
    for (const cm of cloudMeals) {
      if (cm && cm.id && !isExcluded(cm.id)) cloudMap.set(cm.id, cm);
    }

    const mergedMap = new Map<string, Meal>();
    let newlyDiscoveredCount = 0;
    const isMockMeal = (m: Meal) =>
      ['m-1', 'm-2', 'm-3', 'm-4'].includes(m.id) ||
      /oatmeal with blueberries|garlic parmesan fettuccine|grilled chicken salad with quinoa|green tea and sourdough/i.test(m.description || '');

    // 1. Add all valid cloud meals
    for (const cm of cloudMeals) {
      if (!isExcluded(cm.id) && !isMockMeal(cm)) {
        mergedMap.set(cm.id, cm);
      }
    }

    // 2. Union with local meals - NEVER drop local items unless explicitly deleted
    for (const lm of localMeals) {
      if (!lm || !lm.id || isMockMeal(lm) || isExcluded(lm.id)) continue;

      if (!cloudMap.has(lm.id)) {
        // This meal exists locally but was never saved in cloud due to connection drop
        mergedMap.set(lm.id, lm);
        newlyDiscoveredCount++;
        // Queue to upload to cloud
        this.enqueue({
          entityId: lm.id,
          type: 'meal',
          action: 'add',
          data: lm,
          userId,
        });
      } else {
        // Exists in both, keep local if it has newer properties
        mergedMap.set(lm.id, lm);
      }
    }

    // 3. Check safety vault for any forgotten items from this or previous sessions
    const vaultMeals = this.getAllVaultItems<Meal>('meals', userId);
    for (const vm of vaultMeals) {
      if (vm && vm.id && !isExcluded(vm.id) && !mergedMap.has(vm.id) && !isMockMeal(vm)) {
        mergedMap.set(vm.id, vm);
        newlyDiscoveredCount++;
      }
    }

    const result = Array.from(mergedMap.values());
    // Sort descending by date/time
    result.sort((a, b) => {
      const timeA = new Date(a.timestamp_iso || a.date).getTime() || 0;
      const timeB = new Date(b.timestamp_iso || b.date).getTime() || 0;
      return timeB - timeA;
    });

    this.backupToVault('meals', result, userId);
    return { merged: result, newlyDiscoveredCount };
  },

  /**
   * Non-destructive merge for Symptoms
   */
  mergeSymptoms(localSymptoms: Symptom[], cloudSymptoms: Symptom[], userId: string): { merged: Symptom[]; newlyDiscoveredCount: number } {
    const queue = this.getQueue(userId);
    const tombstones = this.getTombstones(userId);
    const deletedSymptomIds = new Set(
      queue.filter((q) => q.type === 'symptom' && q.action === 'delete').map((q) => q.entityId)
    );

    const isExcluded = (id: string) => deletedSymptomIds.has(id) || tombstones.has(id);

    const cloudMap = new Map<string, Symptom>();
    for (const cs of cloudSymptoms) {
      if (cs && cs.id && !isExcluded(cs.id)) cloudMap.set(cs.id, cs);
    }

    const mergedMap = new Map<string, Symptom>();
    let newlyDiscoveredCount = 0;

    const isMockSymptom = (s: Symptom) => ['s-1', 's-2', 's-3'].includes(s.id);

    for (const cs of cloudSymptoms) {
      if (!isExcluded(cs.id) && !isMockSymptom(cs)) {
        mergedMap.set(cs.id, cs);
      }
    }

    for (const ls of localSymptoms) {
      if (!ls || !ls.id || isExcluded(ls.id) || isMockSymptom(ls)) continue;
      if (!cloudMap.has(ls.id)) {
        mergedMap.set(ls.id, ls);
        newlyDiscoveredCount++;
        this.enqueue({
          entityId: ls.id,
          type: 'symptom',
          action: 'add',
          data: ls,
          userId,
        });
      } else {
        mergedMap.set(ls.id, ls);
      }
    }

    const vaultSymptoms = this.getAllVaultItems<Symptom>('symptoms', userId);
    for (const vs of vaultSymptoms) {
      if (vs && vs.id && !isExcluded(vs.id) && !mergedMap.has(vs.id) && !isMockSymptom(vs)) {
        mergedMap.set(vs.id, vs);
        newlyDiscoveredCount++;
      }
    }

    const result = Array.from(mergedMap.values());
    result.sort((a, b) => {
      const timeA = new Date(a.timestamp_iso || a.date).getTime() || 0;
      const timeB = new Date(b.timestamp_iso || b.date).getTime() || 0;
      return timeB - timeA;
    });

    this.backupToVault('symptoms', result, userId);
    return { merged: result, newlyDiscoveredCount };
  },

  /**
   * Non-destructive merge for Exercises
   */
  mergeExercises(localExercises: Exercise[], cloudExercises: Exercise[], userId: string): { merged: Exercise[]; newlyDiscoveredCount: number } {
    const queue = this.getQueue(userId);
    const tombstones = this.getTombstones(userId);
    const deletedIds = new Set(
      queue.filter((q) => q.type === 'exercise' && q.action === 'delete').map((q) => q.entityId)
    );

    const isExcluded = (id: string) => deletedIds.has(id) || tombstones.has(id);

    const cloudMap = new Map<string, Exercise>();
    for (const ce of cloudExercises) {
      if (ce && ce.id && !isExcluded(ce.id)) cloudMap.set(ce.id, ce);
    }

    const mergedMap = new Map<string, Exercise>();
    let newlyDiscoveredCount = 0;

    for (const ce of cloudExercises) {
      if (!isExcluded(ce.id)) {
        mergedMap.set(ce.id, ce);
      }
    }

    for (const le of localExercises) {
      if (!le || !le.id || isExcluded(le.id)) continue;
      if (!cloudMap.has(le.id)) {
        mergedMap.set(le.id, le);
        newlyDiscoveredCount++;
        this.enqueue({
          entityId: le.id,
          type: 'exercise',
          action: 'add',
          data: le,
          userId,
        });
      } else {
        mergedMap.set(le.id, le);
      }
    }

    const vaultExercises = this.getAllVaultItems<Exercise>('exercises', userId);
    for (const ve of vaultExercises) {
      if (ve && ve.id && !isExcluded(ve.id) && !mergedMap.has(ve.id)) {
        mergedMap.set(ve.id, ve);
        newlyDiscoveredCount++;
      }
    }

    const result = Array.from(mergedMap.values());
    result.sort((a, b) => {
      const timeA = new Date(a.timestamp_iso || a.date).getTime() || 0;
      const timeB = new Date(b.timestamp_iso || b.date).getTime() || 0;
      return timeB - timeA;
    });

    this.backupToVault('exercises', result, userId);
    return { merged: result, newlyDiscoveredCount };
  },

  /**
   * Flushes all pending mutations in the queue to Cloudflare KV.
   */
  async flush(userId: string): Promise<{ success: boolean; flushedCount: number }> {
    const queue = this.getQueue(userId);
    if (queue.length === 0) return { success: true, flushedCount: 0 };

    let flushedCount = 0;
    const remainingQueue: SyncQueueItem[] = [];

    const headers = {
      'Content-Type': 'application/json',
      'X-User-Id': userId,
    };

    for (const item of queue) {
      try {
        let endpoint = '';
        if (item.type === 'meal') endpoint = '/api/meals';
        else if (item.type === 'symptom') endpoint = '/api/symptoms';
        else if (item.type === 'exercise') endpoint = '/api/exercises';

        if (!endpoint) {
          flushedCount++;
          continue;
        }

        let res: Response;
        if (item.action === 'add') {
          res = await fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(item.data),
          });
        } else if (item.action === 'update') {
          res = await fetch(endpoint, {
            method: 'PUT',
            headers,
            body: JSON.stringify(item.data),
          });
        } else if (item.action === 'delete') {
          res = await fetch(`${endpoint}?id=${encodeURIComponent(item.entityId)}`, {
            method: 'DELETE',
            headers: { 'X-User-Id': userId },
          });
        } else {
          flushedCount++;
          continue;
        }

        if (res.ok) {
          flushedCount++;
        } else {
          item.retryCount = (item.retryCount || 0) + 1;
          if (item.retryCount < 10) {
            remainingQueue.push(item);
          }
        }
      } catch (err) {
        item.retryCount = (item.retryCount || 0) + 1;
        if (item.retryCount < 10) {
          remainingQueue.push(item);
        }
      }
    }

    this.saveQueue(remainingQueue, userId);
    return { success: remainingQueue.length === 0, flushedCount };
  },
};
